import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { BehaviorSubject } from 'rxjs';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private currentTenantSubject = new BehaviorSubject<string>('');
  public currentTenant$ = this.currentTenantSubject.asObservable();
  // Add central domains that should call the main API without tenant prefix
  private centralDomains = environment.tenancyConfig.centralDomains;

  constructor(private router: Router) {
    // Initialize from localStorage if available
    const storedTenant = localStorage.getItem('access_code');
    if (storedTenant) {
      this.currentTenantSubject.next(storedTenant);
    } else {
      // Try to extract tenant from subdomain
      this.extractTenantFromSubdomain();
    }
  }

  /**
   * Extract tenant from subdomain and store it
   */
  extractTenantFromSubdomain(): void {
    if (environment.tenancyConfig.type === 'subdomain') {
      const hostname = window.location.hostname;
      const host = window.location.host;

      // Skip extraction for central domains
      if (this.isCentralDomain(host)) {
        return;
      }

      // Check if hostname has subdomain structure (e.g., abc.localhost)
      if (hostname.includes('.') && !hostname.startsWith('www.')) {
        // Extract subdomain (the part before the first dot)
        const subdomain = hostname.split('.')[0];
        if (subdomain) {
          this.setTenant(subdomain);
          console.log('Tenant extracted from subdomain:', subdomain);
        }
      }
    }
  }

  /**
   * Check if current host is a central domain
   */
  isCentralDomain(host: string): boolean {
    return this.centralDomains.includes(host);
  }

  /**
   * Set the current tenant
   */
  setTenant(tenantId: string): void {
    localStorage.setItem('access_code', tenantId);
    this.currentTenantSubject.next(tenantId);
  }

  /**
   * Get the current tenant
   */
  getCurrentTenant(): string {
    return this.currentTenantSubject.getValue();
  }

  /**
   * Clear tenant information (used during logout)
   */
  clearTenant(): void {
    localStorage.removeItem('access_code');
    this.currentTenantSubject.next('');
  }

  /**
   * Handle tenant not found error
   * @param errorResponse The error response from API
   */
  handleTenantNotFoundError(errorResponse: any): void {
    // Check if this is a tenant not found error
    if (errorResponse && errorResponse.error && errorResponse.error.code === 'tenant_not_found') {
      // Clear any stored tenant information
      this.clearTenant();

      // Show friendly message to user
      Swal.fire({
        title: 'Tenant Not Found',
        text: 'The requested tenant does not exist. You will be redirected to the main login page.',
        icon: 'error',
        confirmButtonText: 'OK'
      }).then(() => {
        // Remove subdomain and navigate to main domain
        const currentHost = window.location.host;
        const mainDomain = currentHost.substring(currentHost.indexOf('.') + 1);
        const protocol = window.location.protocol;

        // Redirect to main domain login
        window.location.href = `${protocol}//${mainDomain}`;
      });
    }
  }

  /**
   * Navigate to tenant-specific route
   */
  navigateToTenantRoute(route: string[]): void {
    const tenantId = this.getCurrentTenant();
    if (tenantId) {
      // Based on the tenancy type in environment, build the URL
      if (environment.tenancyConfig.type === 'path') {
        // For path-based tenancy
        this.router.navigate([environment.tenancyConfig.pathPrefix, tenantId, ...route]);
      } else {
        // For header-based tenancy (our current approach)
        this.router.navigate(['ac', tenantId, ...route]);
      }
    } else {
      console.error('No tenant selected');
    }
  }

  /**
   * Get tenant-specific API URL
   * This handles cases where the API URL might need to be formatted specifically for the tenant
   */
  getTenantApiUrl(baseUrl: string): string {
    // If current host is a central domain, use the main API URL
    if (this.isCentralDomain(window.location.host)) {
      // For sa.vanisoftware.com -> api.vanisoftware.com
      // For localhost:4200 -> localhost:8000
      if (window.location.host === 'sa.vanisoftware.com' && environment.prodType === 'production') {
        return baseUrl.includes('localhost') ?
          baseUrl : baseUrl.replace(/\/\/[^.]+\./, '//api.');
      }else if (window.location.host === 'sa.vanisoftware.com' && environment.prodType === 'dev') {
        return baseUrl.includes('localhost') ?
          baseUrl : baseUrl.replace(/\/\/[^.]+\./, '//stagingapi.');
      }
      return baseUrl;
    }

    const tenantId = this.getCurrentTenant();

    // Different URL formats based on tenancy type
    switch (environment.tenancyConfig.type) {
      case 'domain':
        return baseUrl.replace('://', `://${tenantId}.`);
      case 'subdomain':
          if (baseUrl.includes('localhost')) {
            return baseUrl.replace('://localhost', `://${tenantId}.localhost`);
          }
          else if (baseUrl.includes(environment.tenancyConfig.domain)) {
            return baseUrl.replace(environment.tenancyConfig.domain, `${tenantId}.${environment.tenancyConfig.domain}`);
          }
          else {
            return `https://${tenantId}.${environment.tenancyConfig.domain}`;
          }
      case 'path':
        return `${baseUrl}/${environment.tenancyConfig.pathPrefix}/${tenantId}`;
      case 'header':
      default:
        // For header-based tenancy, the URL doesn't change
        return baseUrl;
    }
  }
}
